<template>
  <div class="jurisdiction-modal-overlay">
    <div class="jurisdiction-modal-card card shadow-lg">
      <div class="card-body p-4 text-center">
        <div class="mb-4">
          <div class="jurisdiction-icon mb-3">
            <i class="fa-solid fa-location-dot fa-3x text-primary"></i>
          </div>
          <h3 class="fw-bold">Where do you want to drive?</h3>
          <p class="text-muted">Enter your postal code to see if we operate in your area.</p>
        </div>

        <div class="search-container mb-4 position-relative">
          <input
            ref="autocompleteInput"
            type="text"
            class="form-control form-control-lg"
            placeholder="Enter your postal code..."
            :disabled="isValidating"
          />
          <div v-if="isValidating" class="spinner-border spinner-border-sm position-absolute end-0 top-50 translate-middle-y me-3 text-primary"></div>
        </div>

        <div v-if="errorMessage" class="alert alert-warning mb-4" role="alert">
          <i class="fa-solid fa-circle-exclamation me-2"></i>
          {{ errorMessage }}
        </div>

        <button
          @click="validateLocation"
          class="btn btn-primary btn-lg w-100"
          :disabled="!selectedPlace || isValidating"
        >
          {{ isValidating ? 'Checking...' : 'Check Availability' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';

const props = defineProps({
  apiKey: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['validated']);

const autocompleteInput = ref(null);
const selectedPlace = ref(null);
const isValidating = ref(false);
const errorMessage = ref('');

async function loadGoogleMapsScript() {
  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    await initAutocomplete();
    return;
  }

  const scriptId = 'google-maps-places-script';
  if (!document.getElementById(scriptId)) {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${props.apiKey}&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  await new Promise(resolve => {
    const check = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.importLibrary) {
        clearInterval(check);
        resolve();
      }
    }, 100);
  });

  await initAutocomplete();
}

async function initAutocomplete() {
  const { Autocomplete } = await window.google.maps.importLibrary('places');
  const autocomplete = new Autocomplete(autocompleteInput.value, {
    types: ['postal_code'],
    componentRestrictions: { country: 'CA' }
  });

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    if (!place.place_id) {
      selectedPlace.value = null;
      return;
    }
    selectedPlace.value = place;
    errorMessage.value = '';
  });
}

async function validateLocation() {
  if (!selectedPlace.value) return;

  isValidating.value = true;
  errorMessage.value = '';

  try {
    const response = await fetch('/api/independent-driver-signup/validate-jurisdiction', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        placeId: selectedPlace.value.place_id
      })
    });

    const data = await response.json();

    if (response.ok && data.valid) {
      emit('validated');
    } else {
      errorMessage.value = data.message || 'We could not validate your location. Please try another postal code.';
    }
  } catch (error) {
    console.error('Validation error:', error);
    errorMessage.value = 'An error occurred during validation. Please try again.';
  } finally {
    isValidating.value = false;
  }
}

onMounted(async () => {
  await loadGoogleMapsScript();
});
</script>

<style scoped>
.jurisdiction-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.jurisdiction-modal-card {
  width: 100%;
  max-width: 500px;
  border: none;
  border-radius: 15px;
}

.jurisdiction-icon {
  background: #f0f7ff;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.search-container input {
  border-radius: 10px;
  padding-left: 15px;
}
</style>

<style>
/* Global style to ensure Google Places Autocomplete dropdown is above the modal */
.pac-container {
  z-index: 10000 !important;
}
</style>
